import { RouteObject } from 'react-router';
import { RolePolicy } from '~/utils/roles';

/**
 * Custom handle interface for admin routes with permissions
 */
export interface AdminRouteHandle {
  /** Required permissions to access this route */
  permissions: RolePolicy[];
  /** Optional breadcrumb label */
  breadcrumb?: string;
  /** Whether this route should be lazy loaded */
  lazyLoad?: boolean;
}

/**
 * Typed route object that intersects with React Router's RouteObject
 * We use intersection type to maintain compatibility while adding typed handle
 */
export type AdminRouteObject = RouteObject & {
  /** Typed handle with permissions and metadata */
  handle?: AdminRouteHandle;
  /** Child routes using the same typed interface */
  children?: AdminRouteObject[];
};

/**
 * Route path constants for admin user detail pages
 * Using const assertion for literal types
 */
export const AdminUserDetailPaths = {
  base: ':id',
  overview: 'overview',
  details: 'details',
  accounts: 'accounts',
  accountDetails: 'accounts/:accountId',
  accountTrades: 'accounts/:accountId/trades/day/:date',
  transactions: 'transactions',
  subscriptions: 'subscriptions',
  tax: 'tax',
  resetCredits: 'reset-credits',
  investigationLog: 'investigation-log',
  emailLog: 'email-log',
  certificates: 'certificates',
  idv: 'idv',
  notes: 'notes',
  paymentMethods: 'payment-methods',
  payouts: 'payouts',
  payoutDetails: 'payouts/:payoutId',
  banking: 'banking',
  liveAccounts: 'live-accounts',
  backToFunded: 'back-to-funded',
  traderProfile: 'trader-profile',
} as const;

/**
 * Type for all valid admin user detail route paths
 */
export type AdminUserDetailPath = (typeof AdminUserDetailPaths)[keyof typeof AdminUserDetailPaths];
